import type { ClassificacaoDoConflito, TipoDeConflito } from './clashClassificacao'
import { hashHex } from './clashIds'
import { capitalizar, contagem, decimal, feminino } from './clashGramatica'
import { PRIORITIES, PRIORITY_LABELS, type ClashPriority } from './clashTriage'

// ---------------------------------------------------------------------------
// Agrupamento automatico dos conflitos em topicos.
//
// Metodos (o utilizador escolhe; fica gravado):
//
//   sitio     um topico por sitio fisico: dentro do mesmo problema (par de
//             especialidades e tipo de conflito), os pares a menos de R
//             metros uns dos outros, em cadeia (R = 1,5 m por omissao).
//             E onde se resolve em obra: o mesmo atravessamento com a
//             conduta, o isolamento e a curva e um topico; o mesmo tipo
//             de atravessamento a 6 m e outro.
//   elemento  um topico por elemento: o mesmo elemento de rede (ou o
//             mesmo elemento EST, nos pares EST x ARQ).
//   familia   um topico por regra x par de especialidades, opcionalmente
//             x nivel. Serve as listas para validacao (o mesmo assunto
//             repetido).
//   nivel     um topico por nivel por cota.
//   misto     por prioridade: por omissao Critica, Alta e Media por sitio
//             e Baixa por familia ("lista para validacao").
//
// Um grupo com mais de `maxPares` conflitos corta-se pela mediana em
// planta, no eixo de maior extensao, ate ficar abaixo (como nos scripts
// do Cha139).
//
// O titulo diz o problema, os niveis e quantos sitios e interferencias o
// grupo tem ("Tubo electrico EEL Ø125 atravessa pavimento e parede ARQ,
// P1: 1 atravessamento (7 interferencias)"). A regra (codigo e
// descricao) vai para a descricao, nao para o titulo.
//
// IDENTIDADE. O id de um grupo novo e o hash dos GlobalIds ordenados dos
// seus elementos (com o Guid do topico BCF derivado dele, estavel). Ao
// voltar a agrupar, um grupo que mantem a maioria dos pares de um grupo
// anterior (mais de metade dos dele e mais de metade dos do anterior)
// fica com o id antigo, e com isso com o mesmo Guid; senao e um grupo
// novo. Os grupos editados a mao (juntados, renomeados, ou com conflitos
// tirados) ficam fixos: voltar a agrupar nao lhes toca.
//
// Puro: quem chama da os conflitos ja classificados.
// ---------------------------------------------------------------------------

export type MetodoBase = 'sitio' | 'elemento' | 'familia' | 'nivel'
export type MetodoDeAgrupamento = MetodoBase | 'misto'

export interface ClashAgrupamentoConfig {
  metodo: MetodoDeAgrupamento
  /** Raio do sitio fisico, m. */
  raioM: number
  /** Familia: separar tambem por nivel. */
  familiaComNivel: boolean
  /** Acima disto, corte pela mediana em planta. */
  maxPares: number
  /** Metodo por prioridade, no misto. */
  misto: Record<ClashPriority, MetodoBase>
}

export const AGRUPAMENTO_POR_OMISSAO: ClashAgrupamentoConfig = {
  metodo: 'misto',
  raioM: 1.5,
  familiaComNivel: false,
  maxPares: 40,
  misto: { critica: 'sitio', alta: 'sitio', media: 'sitio', baixa: 'familia' },
}

export const NOME_DO_METODO: Record<MetodoDeAgrupamento, string> = {
  sitio: 'por sítio físico',
  elemento: 'por elemento',
  familia: 'por família (regra x especialidades)',
  nivel: 'por nível',
  misto: 'misto, por prioridade',
}

const METODOS_BASE: MetodoBase[] = ['sitio', 'elemento', 'familia', 'nivel']

export function lerAgrupamentoConfig(v: unknown): ClashAgrupamentoConfig {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>
  const out: ClashAgrupamentoConfig = { ...AGRUPAMENTO_POR_OMISSAO, misto: { ...AGRUPAMENTO_POR_OMISSAO.misto } }
  if (o.metodo === 'misto' || METODOS_BASE.includes(o.metodo as MetodoBase)) out.metodo = o.metodo as MetodoDeAgrupamento
  if (typeof o.raioM === 'number' && o.raioM > 0 && o.raioM <= 100) out.raioM = o.raioM
  if (typeof o.familiaComNivel === 'boolean') out.familiaComNivel = o.familiaComNivel
  if (typeof o.maxPares === 'number' && o.maxPares >= 2) out.maxPares = Math.floor(o.maxPares)
  const m = o.misto as Record<string, unknown> | undefined
  if (m && typeof m === 'object') {
    for (const p of PRIORITIES) if (METODOS_BASE.includes(m[p] as MetodoBase)) out.misto[p] = m[p] as MetodoBase
  }
  return out
}

/** Um conflito para agrupar. */
export interface ConflitoParaAgrupar {
  key: string
  /** Elementos do par ("modelo:localId"). */
  elementoA: string
  elementoB: string
  /** GlobalIds dos dois elementos, quando conhecidos (identidade). */
  guids: [string | null, string | null]
  kind: 'hard' | 'clearance'
  depth?: number | null
  /** Ponto do conflito (Z para cima). */
  point: [number, number, number]
  classificacao: ClassificacaoDoConflito
  /** Prioridade efectiva (a da triagem, se houver, senao a da regra). */
  prioridade: ClashPriority | null
}

export interface GrupoCalculado {
  id: string
  /** Hash dos GlobalIds ordenados (identidade da composicao). */
  identidade: string
  titulo: string
  descricao: string
  chaves: string[]
  prioridade: ClashPriority | null
  metodo: MetodoBase
}

/** Grupo ja existente, para manter ids e respeitar as edicoes a mao. */
export interface GrupoAnterior {
  id: string
  chaves: readonly string[]
  /** Editado a mao: fica como esta. */
  manual?: boolean
}

// --- Utilitarios ----------------------------------------------------------------

const ORDEM_PRIO: ClashPriority[] = ['critica', 'alta', 'media', 'baixa']

function maisAlta(ps: readonly (ClashPriority | null)[]): ClashPriority | null {
  let out: ClashPriority | null = null
  for (const p of ps) {
    if (p && (out === null || ORDEM_PRIO.indexOf(p) < ORDEM_PRIO.indexOf(out))) out = p
  }
  return out
}

function dist(a: readonly number[], b: readonly number[]): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])
}

/** Componentes ligados (cadeia) por distancia < raio. Grelha espacial:
 *  milhares de conflitos sem O(n^2) no caso comum. */
export function sitiosPorDistancia<T extends { point: readonly number[] }>(itens: readonly T[], raio: number): T[][] {
  const n = itens.length
  const pai = Array.from({ length: n }, (_, i) => i)
  const raiz = (i: number): number => {
    while (pai[i] !== i) {
      pai[i] = pai[pai[i]]
      i = pai[i]
    }
    return i
  }
  const unir = (i: number, j: number) => {
    const a = raiz(i)
    const b = raiz(j)
    if (a !== b) pai[Math.max(a, b)] = Math.min(a, b)
  }
  const celula = (p: readonly number[]) => p.map((x) => Math.floor(x / raio))
  const grelha = new Map<string, number[]>()
  itens.forEach((it, i) => {
    const k = celula(it.point).join(',')
    const l = grelha.get(k)
    if (l) l.push(i)
    else grelha.set(k, [i])
  })
  itens.forEach((it, i) => {
    const [cx, cy, cz] = celula(it.point)
    for (let dx = -1; dx <= 1; dx++)
      for (let dy = -1; dy <= 1; dy++)
        for (let dz = -1; dz <= 1; dz++) {
          for (const j of grelha.get(`${cx + dx},${cy + dy},${cz + dz}`) ?? []) {
            if (j > i && dist(it.point, itens[j].point) < raio) unir(i, j)
          }
        }
  })
  const grupos = new Map<number, T[]>()
  itens.forEach((it, i) => {
    const r = raiz(i)
    const l = grupos.get(r)
    if (l) l.push(it)
    else grupos.set(r, [it])
  })
  return [...grupos.values()]
}

/** Corte pela mediana em planta, no eixo de maior extensao, ate cada
 *  parte ter no maximo `max` itens. */
export function cortarPelaMediana<T extends { point: readonly number[] }>(itens: T[], max: number): T[][] {
  if (itens.length <= max) return [itens]
  const xs = itens.map((c) => c.point[0])
  const ys = itens.map((c) => c.point[1])
  const ax = Math.max(...xs) - Math.min(...xs) >= Math.max(...ys) - Math.min(...ys) ? 0 : 1
  const s = [...itens].sort((a, b) => a.point[ax] - b.point[ax] || a.point[1 - ax] - b.point[1 - ax])
  const h = Math.floor(s.length / 2)
  return [...cortarPelaMediana(s.slice(0, h), max), ...cortarPelaMediana(s.slice(h), max)]
}

/** O elemento que da nome ao conflito no metodo "por elemento": o de
 *  rede (rede x EST/ARQ), o EST (EST x ARQ), o que cede (rede x rede). */
function elementoChave(c: ConflitoParaAgrupar): string {
  const k = c.classificacao
  if (k.rede) return `${k.rede.modelId}:${k.rede.localId}`
  if (k.especialidadeA && k.especialidadeA === k.responsavel) return k.idA
  if (k.especialidadeB && k.especialidadeB === k.responsavel) return k.idB
  return k.idA
}

function chaveDoMetodo(c: ConflitoParaAgrupar, metodo: MetodoBase, cfg: ClashAgrupamentoConfig): string {
  const k = c.classificacao
  const par = `${k.especialidadeA ?? '?'}×${k.especialidadeB ?? '?'}`
  switch (metodo) {
    case 'sitio':
      return `sitio|${par}|${k.tipo}`
    case 'elemento':
      return `elemento|${elementoChave(c)}|${c.kind}`
    case 'familia':
      return `familia|${k.regra}|${par}${cfg.familiaComNivel ? `|${k.nivel ?? ''}` : ''}`
    case 'nivel':
      return `nivel|${k.nivel ?? ''}`
  }
}

/** Identidade de uma composicao: hash dos GlobalIds ordenados (ou das
 *  chaves, sem GlobalIds). */
export function identidadeDoGrupo(membros: readonly { key: string; guids: [string | null, string | null] }[]): string {
  const ids = new Set<string>()
  for (const m of membros) {
    const [a, b] = m.guids
    if (a && b) {
      ids.add(`${[a, b].sort().join('+')}`)
    } else {
      ids.add(`k:${m.key}`)
    }
  }
  return hashHex([...ids].sort().join('|')).slice(0, 12)
}

// --- Titulos ----------------------------------------------------------------------

function juntarComE(xs: readonly string[]): string {
  if (xs.length <= 1) return xs[0] ?? ''
  return `${xs.slice(0, -1).join(', ')} e ${xs[xs.length - 1]}`
}

function unicos<T>(xs: readonly T[]): T[] {
  return [...new Set(xs)]
}

/** Papeis que sao parte de outro na leitura do titulo: o isolamento e o
 *  acessorio sao da conduta, a curva e do tubo. */
const PAPEL_PRINCIPAL: Record<string, string> = {
  'acessório de conduta': 'conduta',
  'isolamento de conduta': 'conduta',
  'curva de tubo eléctrico': 'tubo eléctrico',
  'revestimento de piso': 'pavimento',
  'revestimento de parede': 'parede',
  'patamar de escada': 'escada',
}

const LINEARES = new Set(['conduta', 'tubagem DAR', 'tubo eléctrico'])

/** Papeis de um lado, reduzidos ao principal e sem a sigla da
 *  especialidade (que vai uma vez no fim). Ordenados por frequencia. */
function papeisDoLado(papeis: readonly string[], esp: string): string[] {
  const n = new Map<string, number>()
  const sufixo = ` ${esp}`
  for (const p0 of papeis) {
    let p = PAPEL_PRINCIPAL[p0] ?? p0
    if (p.endsWith(sufixo)) p = p.slice(0, -sufixo.length)
    n.set(p, (n.get(p) ?? 0) + 1)
  }
  return [...n.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([p]) => p)
}

function lado(papeis: readonly string[], esp: string, sep: 'e' | '/' = 'e'): string {
  const ps = papeisDoLado(papeis, esp)
  const t = sep === 'e' ? juntarComE(ps) : ps.join('/')
  return ps.every((p) => p.includes(esp)) ? t : `${t} ${esp}`
}

/** Participio concordante com os papeis do sujeito: "sobreposta",
 *  "sobrepostos". */
function participio(raiz: string, papeis: readonly string[]): string {
  const plural = papeis.length > 1
  const fem = papeis.every((p) => feminino(p))
  return `${raiz}${fem ? 'a' : 'o'}${plural ? 's' : ''}`
}

function papelDaRede(c: ConflitoParaAgrupar): string {
  const k = c.classificacao
  return k.rede && k.especialidadeA === k.rede.especialidade ? k.papelA : k.papelB
}

function diametros(cs: readonly ConflitoParaAgrupar[]): string {
  const ds = unicos(
    cs
      .filter((c) => {
        if (!c.classificacao.rede) return false
        const p = papelDaRede(c)
        return LINEARES.has(PAPEL_PRINCIPAL[p] ?? p)
      })
      .map((c) => c.classificacao.diametroMm)
      .filter((d): d is number => d !== null),
  ).sort((a, b) => a - b)
  if (ds.length === 0) return ''
  if (ds.length === 1) return ` Ø${ds[0]}`
  return ` Ø${ds[0]} a Ø${ds[ds.length - 1]}`
}

/** "Tubo electrico EEL Ø125 atravessa pavimento e parede ARQ". */
export function problemaDoGrupo(cs: readonly ConflitoParaAgrupar[]): string {
  const rep = cs[0].classificacao
  const tipo = rep.tipo
  const espA = rep.especialidadeA ?? '?'
  const espB = rep.especialidadeB ?? '?'
  const papeisA = cs.map((c) => c.classificacao.papelA)
  const papeisB = cs.map((c) => c.classificacao.papelB)
  if (rep.rede && rep.hospedeiro) {
    const redeEsp = rep.rede.especialidade ?? '?'
    const hospEsp = rep.hospedeiro.especialidade ?? '?'
    const redeA = rep.especialidadeA === redeEsp
    const papeisRede = redeA ? papeisA : papeisB
    const papeisHosp = redeA ? papeisB : papeisA
    const sujeitos = papeisDoLado(papeisRede, redeEsp)
    const sujeito = `${capitalizar(lado(papeisRede, redeEsp))}${diametros(cs)}`
    const objecto = lado(papeisHosp, hospEsp)
    const pl = sujeitos.length > 1
    const verbo: Partial<Record<TipoDeConflito, string>> = {
      atravessamento: pl ? 'atravessam' : 'atravessa',
      embebido: `${participio('embebid', sujeitos)} em`,
      interferência: pl ? 'interferem com' : 'interfere com',
      'duplicação/sobreposição': `${participio('sobrepost', sujeitos)} a`,
    }
    return `${sujeito} ${verbo[tipo] ?? (pl ? 'interferem com' : 'interfere com')} ${objecto}`
  }
  const sujeitos = papeisDoLado(papeisA, espA)
  const pl = sujeitos.length > 1
  const a = lado(papeisA, espA)
  const b = lado(papeisB, espB)
  switch (tipo) {
    case 'folga':
      return `Folga insuficiente entre ${lado(papeisA, espA, '/')} e ${lado(papeisB, espB, '/')}`
    case 'duplicação':
      return `${capitalizar(a)} ${participio('duplicad', sujeitos)} em ${b} (duplicação de modelação)`
    case 'entrega/apoio':
      return `${capitalizar(a)} com entrega em ${b}`
    case 'sobreposição':
      return `${capitalizar(a)} ${participio('sobrepost', sujeitos)} a ${b}`
    default:
      return `${capitalizar(a)} ${pl ? 'interferem com' : 'interfere com'} ${b}`
  }
}

function nomeDoSitio(tipo: TipoDeConflito): [string, string] {
  if (tipo === 'atravessamento') return ['atravessamento', 'atravessamentos']
  if (tipo === 'entrega/apoio') return ['apoio', 'apoios']
  return ['sítio', 'sítios']
}

/** Niveis por ordem de cota; tres ou mais seguidos ficam "P1 a P5". */
export function textoDosNiveis(niveis: readonly string[], ordem: readonly string[] = []): string {
  const pos = (n: string) => {
    const i = ordem.indexOf(n)
    return i < 0 ? ordem.length : i
  }
  const ns = unicos(niveis).sort((a, b) => pos(a) - pos(b) || a.localeCompare(b))
  if (ns.length >= 3 && ordem.length > 0) {
    const idx = ns.map(pos)
    const seguidos = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1) && idx[0] < ordem.length
    if (seguidos) return `${ns[0]} a ${ns[ns.length - 1]}`
  }
  return juntarComE(ns)
}

/** Titulo do grupo: problema, niveis, sitios e interferencias. */
export function tituloDoGrupo(
  cs: readonly ConflitoParaAgrupar[],
  raio: number,
  extras: { zona?: [number, number]; validacao?: boolean; ordemNiveis?: readonly string[] } = {},
): string {
  const ordenados = ordenarPorGravidade(cs)
  const rep = ordenados[0].classificacao
  const niveis = ordenados.map((c) => c.classificacao.nivel).filter((n): n is string => !!n)
  const sitios = sitiosPorDistancia(cs, raio).length
  const [s1, s2] = nomeDoSitio(rep.tipo)
  const duros = cs.every((c) => c.kind === 'clearance') ? ['ocorrência', 'ocorrências'] : ['interferência', 'interferências']
  let cabeca = problemaDoGrupo(ordenados)
  if (niveis.length > 0) cabeca += `, ${textoDosNiveis(niveis, extras.ordemNiveis)}`
  if (extras.zona) cabeca += `, zona ${extras.zona[0]} de ${extras.zona[1]}`
  let t = `${cabeca}: ${contagem(sitios, s1, s2)} (${contagem(cs.length, duros[0], duros[1])})`
  if (extras.validacao) t += ', lista para validação'
  return t
}

function ordenarPorGravidade(cs: readonly ConflitoParaAgrupar[]): ConflitoParaAgrupar[] {
  return [...cs].sort((a, b) => {
    const pa = a.prioridade ? ORDEM_PRIO.indexOf(a.prioridade) : 9
    const pb = b.prioridade ? ORDEM_PRIO.indexOf(b.prioridade) : 9
    return pa - pb || (b.depth ?? 0) - (a.depth ?? 0) || a.key.localeCompare(b.key)
  })
}

/** Descricao do grupo: metodo, regras (codigo e texto) e contagens. */
export function descricaoDoGrupo(
  cs: readonly ConflitoParaAgrupar[],
  metodo: MetodoBase,
  cfg: ClashAgrupamentoConfig,
  descricaoDaRegra: (regra: string) => string,
): string {
  const linhas: string[] = []
  linhas.push(
    `Agrupamento ${NOME_DO_METODO[metodo]}${metodo === 'sitio' ? ` (pares a menos de ${decimal(cfg.raioM, 1)} m)` : ''}.`,
  )
  const regras = unicos(ordenarPorGravidade(cs).map((c) => c.classificacao.regra))
  for (const r of regras) linhas.push(`Regra ${r}: ${descricaoDaRegra(r)}`)
  const porPrio = new Map<string, number>()
  for (const c of cs) {
    const p = c.prioridade ? PRIORITY_LABELS[c.prioridade] : 'sem prioridade'
    porPrio.set(p, (porPrio.get(p) ?? 0) + 1)
  }
  if (porPrio.size > 1) {
    linhas.push(`Prioridade por conflito: ${[...porPrio].map(([p, n]) => `${p} ${n}`).join(', ')}.`)
  }
  return linhas.join('\n')
}

// --- Agrupar ----------------------------------------------------------------------

/**
 * Agrupa os conflitos. `fixos`: chaves que nao se mexem (editadas a mao).
 * `anteriores`: os grupos que ja existiam, para manter os ids.
 */
export function agrupar(
  conflitos: readonly ConflitoParaAgrupar[],
  cfg: ClashAgrupamentoConfig,
  opts: {
    fixos?: ReadonlySet<string>
    anteriores?: readonly GrupoAnterior[]
    descricaoDaRegra?: (regra: string) => string
    /** Nomes dos niveis por ordem de cota (titulos). */
    ordemNiveis?: readonly string[]
  } = {},
): GrupoCalculado[] {
  const fixos = opts.fixos ?? new Set<string>()
  const manuais = new Set((opts.anteriores ?? []).filter((g) => g.manual).flatMap((g) => g.chaves))
  const livres = conflitos.filter(
    (c) => !fixos.has(c.key) && !manuais.has(c.key) && c.classificacao.prioridade !== null,
  )
  const metodoDe = (c: ConflitoParaAgrupar): MetodoBase => {
    if (cfg.metodo !== 'misto') return cfg.metodo
    return cfg.misto[c.prioridade ?? 'baixa']
  }
  // 1. Particao pela chave do metodo (no misto, cada conflito pelo metodo
  //    da sua prioridade).
  const blocos = new Map<string, { metodo: MetodoBase; itens: ConflitoParaAgrupar[] }>()
  for (const c of livres) {
    const m = metodoDe(c)
    const k = `${m}|${chaveDoMetodo(c, m, cfg)}`
    const b = blocos.get(k)
    if (b) b.itens.push(c)
    else blocos.set(k, { metodo: m, itens: [c] })
  }
  // 2. Sitios dentro de cada bloco (metodo sitio), corte pela mediana.
  const brutos: { metodo: MetodoBase; itens: ConflitoParaAgrupar[]; zona?: [number, number] }[] = []
  for (const b of [...blocos.keys()].sort()) {
    const { metodo, itens } = blocos.get(b)!
    const partes = metodo === 'sitio' ? sitiosPorDistancia(itens, cfg.raioM) : [itens]
    for (const p of partes) {
      const cortes = cortarPelaMediana(p, cfg.maxPares)
      cortes.forEach((parte, i) =>
        brutos.push({ metodo, itens: parte, ...(cortes.length > 1 ? { zona: [i + 1, cortes.length] } : {}) }),
      )
    }
  }
  // 3. Ids: maioria com um grupo anterior, senao o hash da composicao.
  const anteriores = (opts.anteriores ?? []).filter((g) => !g.manual)
  const usados = new Set<string>()
  const descricao = opts.descricaoDaRegra ?? ((r: string) => r)
  const out: GrupoCalculado[] = []
  for (const g of brutos) {
    const chaves = g.itens.map((c) => c.key)
    const set = new Set(chaves)
    const identidade = identidadeDoGrupo(g.itens)
    let id: string | null = null
    for (const a of anteriores) {
      if (usados.has(a.id)) continue
      const comuns = a.chaves.filter((k) => set.has(k)).length
      if (comuns * 2 > chaves.length && comuns * 2 > a.chaves.length) {
        id = a.id
        break
      }
    }
    if (!id) {
      id = `G-${identidade.slice(0, 8)}`
      let n = 2
      while (usados.has(id)) id = `G-${identidade.slice(0, 8)}-${n++}`
    }
    usados.add(id)
    const ordenados = ordenarPorGravidade(g.itens)
    const validacao = cfg.metodo === 'misto' && g.metodo === 'familia'
    out.push({
      id,
      identidade,
      titulo: tituloDoGrupo(ordenados, cfg.raioM, { zona: g.zona, validacao, ordemNiveis: opts.ordemNiveis }),
      descricao: descricaoDoGrupo(ordenados, g.metodo, cfg, descricao),
      chaves: ordenados.map((c) => c.key),
      prioridade: maisAlta(ordenados.map((c) => c.prioridade)),
      metodo: g.metodo,
    })
  }
  out.sort(
    (a, b) =>
      (a.prioridade ? ORDEM_PRIO.indexOf(a.prioridade) : 9) - (b.prioridade ? ORDEM_PRIO.indexOf(b.prioridade) : 9) ||
      a.titulo.localeCompare(b.titulo),
  )
  return out
}

/** Contagem de topicos por prioridade (para o resumo). */
export function topicosPorPrioridade(grupos: readonly GrupoCalculado[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const g of grupos) {
    const p = g.prioridade ? PRIORITY_LABELS[g.prioridade] : 'Sem prioridade'
    out[p] = (out[p] ?? 0) + 1
  }
  return out
}
